import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { contactForms } from '../data/mock';
import { Send, MessageSquare, FileCheck } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import SuccessModal from './SuccessModal';

const ContactSection = () => {
  const [activeForm, setActiveForm] = useState('demo');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedRequestTopic, setSubmittedRequestTopic] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Get backend URL from environment variable
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Send form data to backend
      const response = await fetch(`${backendUrl}/api/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_type: formType,
          form_data: formData
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Store request topic for modal
        setSubmittedRequestTopic(formData.requestTopic || 'your inquiry');
        
        // Show success modal
        setShowSuccessModal(true);
        
        // Clear form data
        setFormData({});
      } else {
        // Show error toast
        toast({
          title: "Submission Failed",
          description: result.detail || "There was an error submitting your request. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Failed",
        description: "Unable to submit your request. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderForm = (formType) => {
    const form = contactForms[formType];
    
    return (
      <form onSubmit={(e) => handleSubmit(e, formType)} className="space-y-6">
        {form.fields.map((field) => (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={field.name} className="text-gray-300">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </Label>
            
            {field.type === 'select' ? (
              <Select onValueChange={(value) => handleInputChange(field.name, value)}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder={`Select ${field.label}`} />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {field.options.map((option) => (
                    <SelectItem key={option} value={option} className="text-white hover:bg-slate-600">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === 'textarea' ? (
              <Textarea
                id={field.name}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                className="bg-slate-700 border-slate-600 text-white placeholder-gray-400"
                rows={4}
                value={formData[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                required={field.required}
              />
            ) : (
              <Input
                id={field.name}
                type={field.type}
                placeholder={`Enter ${field.label.toLowerCase()}`}
                className="bg-slate-700 border-slate-600 text-white placeholder-gray-400"
                value={formData[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                required={field.required}
              />
            )}
          </div>
        ))}
        
        <Button 
          type="submit" 
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Submitting...
            </div>
          ) : (
            <div className="flex items-center">
              <Send className="mr-2" size={16} />
              Submit {form.title}
            </div>
          )}
        </Button>
      </form>
    );
  };

  return (
    <section id="contact" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800" />
        {/* Animated background elements */}
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 2, 1],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: Math.random() * 4 + 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get Started with <span className="text-cyan-400">MSML</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to improve efficiency of your AI and Media Applications? Contact us to explore how MSML can transform your business.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Demo Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Card className="bg-slate-900/50 border-cyan-400/20 backdrop-blur-sm h-full">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center">
                  <MessageSquare className="mr-3 text-cyan-400" size={28} />
                  {contactForms.demo.title}
                </CardTitle>
                <CardDescription className="text-gray-300">
                  {contactForms.demo.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderForm('demo')}
              </CardContent>
            </Card>
          </motion.div>

          {/* License Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Card className="bg-slate-900/50 border-blue-400/20 backdrop-blur-sm h-full">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center">
                  <FileCheck className="mr-3 text-blue-400" size={28} />
                  {contactForms.license.title}
                </CardTitle>
                <CardDescription className="text-gray-300">
                  {contactForms.license.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderForm('license')}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Additional Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-slate-900/30 rounded-xl p-8 border border-cyan-400/10">
            <h3 className="text-xl font-semibold text-white mb-4">Enterprise Solutions</h3>
            <p className="text-gray-300 mb-4">
              For enterprise implementations, custom integrations, or partnership opportunities, 
              our team is ready to discuss your specific requirements.
            </p>
            <p className="text-cyan-400 font-medium">
              Email: enterprise@generalharmonics.com | Phone: +1 (519) 870 5831
            </p>
          </div>
        </motion.div>
      </div>

      {/* Success Modal */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        requestTopic={submittedRequestTopic}
      />
    </section>
  );
};

export default ContactSection;